import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { formatINR } from "@/lib/format";

function Row({ label, value, sign, cls = "", testid }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-[#EAE7DD] last:border-0">
      <span className="text-sm">{label}</span>
      <span className={`num text-sm font-medium ${cls}`} data-testid={testid}>
        {sign}{formatINR(Math.abs(value))}
      </span>
    </div>
  );
}

/** Pure render of a cash statement payload — used by Statements and the shared CA view. */
export function CashStatementView({ data }) {
  if (!data) return null;
  return (
    <section className="border border-[#D8D4C7] bg-white p-6" data-testid="cash-statement">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="font-serif text-2xl tracking-tight">Cash statement.</h3>
        <span className="font-mono text-[9px] uppercase tracking-widest text-[#6C7565]">All cash accounts · linked entries</span>
      </div>
      <Row label="Opening balance" value={data.opening_balance} sign={data.opening_balance < 0 ? "−" : ""} testid="cs-opening" />
      <Row label="Money in" value={data.money_in} sign="+" cls="text-[#2E6F40]" testid="cs-in" />
      <Row label="Money out" value={data.money_out} sign="−" cls="text-[#D94F3D]" testid="cs-out" />
      {data.transfers_net !== 0 && (
        <Row label="Transfers (net)" value={data.transfers_net} sign={data.transfers_net > 0 ? "+" : "−"} cls="text-[#6C7565]" testid="cs-transfers" />
      )}
      <div className="flex items-center justify-between pt-3 mt-1 border-t-2 border-[#1A1F16]">
        <span className="text-sm font-medium uppercase tracking-widest font-mono text-[10px]">Closing balance</span>
        <span className="num font-serif text-2xl" data-testid="cs-closing">{formatINR(data.closing_balance)}</span>
      </div>
    </section>
  );
}

/** Bank-statement style summary of all cash accounts for the selected range. */
export function CashStatement({ range }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/reports/cash-statement?range=${range}`);
      setData(data);
    } catch (e) {
      logError("Cash statement load failed", e);
    }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  return <CashStatementView data={data} />;
}
